import {Image, Slot, Trail} from '@yandex-delivery/blocks'
import {anyPass, equals, flow, get, pipe, reduce} from '@yandex-delivery/utils'
import type {ReactElement} from 'react'

import {Body} from '../../../components/desktop'
import {tagToUrl} from '../../../lib/tagToUrl'
import {SegmentWidget} from '../../Segment/SegmentWidget@desktop'
import {renderBody, SlotWidget} from '../../Slot/SlotWidget@desktop'
import {
  type ImageWidgetConfig,
  type SegmentWidgetConfig,
  type SlotWidgetConfig,
  type TextWidgetConfig,
  type WidgetConfig,
  WidgetType,
} from '../../types'
import * as classes from './Requirements@desktop.classes'

type Widget = SlotWidgetConfig | ImageWidgetConfig
type Props = {
  widgets: Widget[]
}

export function Requirements({widgets}: Props): ReactElement {
  const {tail, children} = splitByWidgetType(widgets)

  return <Body tail={<>{tail}</>}>{children}</Body>
}

type WidgetElements = {
  tail: ReactElement[]
  children: ReactElement | null
}

function splitByWidgetType(widgets: Widget[]): WidgetElements {
  const initialValue: WidgetElements = {
    tail: [],
    children: null,
  }

  return pipe(
    widgets,
    reduce((acc: WidgetElements, widget: Widget) => {
      if (isImageWidget(widget)) {
        const alt = pipe(
          widget,
          get('text.items'),
          reduce((accum: string, item: TextWidgetConfig): string => accum + item.text, ''),
        )

        const imageTag = widget.image.image_tag
        acc.children = (
          <div className={classes.imgContainer}>
            <Image alt={alt} src={tagToUrl(imageTag)} width={getImageWidthByTag(imageTag)} />
          </div>
        )
      }

      if (isCargoWidget(widget)) {
        const trailWidget = widget.slot.trail1

        acc.tail.push(
          <Slot key={widget.widget_id} className={classes.slot} mt={acc.tail.length === 0 ? undefined : 'xs'} pl="m">
            {renderBody(widget)}
            {isSegment(trailWidget) && (
              <Trail className={classes.trail[widget.widget_id]} mr="m">
                <SegmentWidget className={classes.segment} config={trailWidget} />
              </Trail>
            )}
          </Slot>,
        )
      } else if (isWalkingCourierWidget(widget)) {
        acc.tail.push(
          <Slot key={widget.widget_id} className={classes.noDividerSlot}>
            <SlotWidget config={widget} />
          </Slot>,
        )
      } else if (isSlotWidget(widget)) {
        acc.tail.push(<SlotWidget key={widget.widget_id} config={widget} />)
      }

      return acc
    }, initialValue),
  )
}

function getImageWidthByTag(tag: string) {
  switch (tag) {
    case 'delivery_walking_courier_image':
      return 310
    case 'delivery_cargo_form_S':
    case 'delivery_cargo_form_M':
      return 280
    case 'delivery_cargo_form_L':
    case 'delivery_cargo_form_XL':
      return 270
    case 'delivery_cargo_form_XXL':
      return 230
    default:
      return 210
  }
}

const isSegment = flow(
  get('type'), //
  equals(WidgetType.SEGMENT),
  // equals не умеет работать с "is"
) as unknown as (widget?: WidgetConfig) => widget is SegmentWidgetConfig

const isCargoWidget = flow(
  get('widget_id'),
  anyPass([
    equals('body_size_widget'), //
    equals('loaders_widget'),
  ]),
  // equals не умеет работать с "is"
) as unknown as (widget: WidgetConfig) => widget is SlotWidgetConfig

const isWalkingCourierWidget = (widget: WidgetConfig): widget is SlotWidgetConfig =>
  !!widget.widget_id?.includes('cargo_c2c_order_forms_b2b_lk_walking_courier_description')

const isSlotWidget = flow(
  get('type'),
  equals(WidgetType.SLOT),
  // equals не умеет работать с "is"
) as unknown as (widget: WidgetConfig) => widget is SlotWidgetConfig

const isImageWidget = flow(
  get('type'),
  equals(WidgetType.IMAGE),
  // equals не умеет работать с "is"
) as unknown as (widget: WidgetConfig) => widget is ImageWidgetConfig
